import React, {useEffect, useState} from "react";
import {
    Button,
    Card,
    Col,
    Modal,
    Spinner,
    Stack
} from "react-bootstrap";

import RulesListElement from "../../components/Rules/RulesListElement";
import {useParams} from "react-router-dom";
import tableDataModules from "../../controller/tableDataModules";
import {RulesProvider} from "../../components/Rules/RulesContext";
import ProportionalSlider from "../../components/elements/ProportionalSlider";

const colorOptions = [
    '#007bff',  // Blue (Primary)
    '#28a745',  // Green (Success)
    '#dc3545',  // Red (Danger)
    '#ffc107',  // Yellow (Warning)
    '#17a2b8',  // Light Blue (Info)
    '#6c757d',  // Gray (Secondary)
    '#343a40',  // Dark (Dark)
    '#fd7e14',  // Orange
    '#20c997',  // Teal
    '#e83e8c',  // Pink
    '#6f42c1',  // Purple
    '#1e7e34',  // Dark Green
    '#d39e00',  // Dark Yellow
    '#117a8b',  // Dark Cyan
    '#1e7e34'   // Forest Green
];


function Rules() {
    let { caseid } = useParams();
    let caseID = caseid;

    const [Structures, setStructures] = useState({});
    const [fU, fUpdate] = useState(true);
    const [saveBtnState, setSaveBtnState] = useState(false);
    const [importBtnState, setImportBtnState] = useState(false);
    const [exportBtnState, setExportBtnState] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingImportFile, setPendingImportFile] = useState(null);
    const [existingRulesCount, setExistingRulesCount] = useState(0);
    const [organWeights, setOrganweights] = useState({});

    useEffect(() => {
        loadInitialData();
        // eslint-disable-next-line
    }, []);

    const loadInitialData = async () => {
        try {
            const val = await tableDataModules.getRulesByCase(caseID);
            const structs = await tableDataModules.getStructsByCase(caseID);


            let structarray = {};
            if(structs.data != null) {
                for (const structName in structs.data) {
                    structarray[structs.data[structName]] = [];
                }
            }
            for (let i = 0; i < val.data.length; i++) {
                if(structarray[val.data[i].organ]){
                    structarray[val.data[i].organ].push(val.data[i]);
                }else{
                    console.log(val.data[i].organ, "not found!")
                }

            }
            setStructures(structarray);
            setOrganweights((await tableDataModules.getRuleOrganWeightsByCase(caseID)).data);
        } catch (error) {
            console.error(error);
            alert("No template file has been found on server or does not contain structs!");
        }
    };

    function callbackRLE(nStruct) {
        setStructures(Structures);
        fUpdate(!fU);
    }

    async function saveBtn() {
        setSaveBtnState(true);
        try {
            // update organ proportions
            await tableDataModules.editOrganWeightByCase(caseID, organWeights);
            // update rules
            for (const Struct of Object.values(Structures)) {
                for (let i = 0; i < Struct.length; i++) {
                    if(Struct[i].created) {
                        let createdRule = await tableDataModules.createRule(Struct[i], caseID);
                        if(createdRule && createdRule.data) {
                            Struct[i] = createdRule.data;
                            Struct[i].created = undefined;
                            callbackRLE(Struct[i]);
                        } else {
                            throw new Error("Failed to create rule");
                        }
                    } else if (Struct[i].delete) {
                        const resp_delete = await tableDataModules.deleteRule(Struct[i].id);
                        if(resp_delete.data === 'ruleresults'){
                            /* eslint-disable-next-line no-restricted-globals */
                            if(confirm("Student results are already generated based on this rule, removing it will require reprocessing!")){
                                await tableDataModules.deleteRule(Struct[i].id,true);
                            }

                        }
                    } else if (Struct[i].modified) {
                        await tableDataModules.saveRule(Struct[i]);
                        Struct[i].modified = undefined;
                        callbackRLE(Struct[i]);
                    }
                }
            }
        } catch (error) {
            alert("Something went wrong while saving: " + error.message);
        }
        setSaveBtnState(false);
    }

    const handleExport = async () => {
        setExportBtnState(true);
        await saveBtn();
        try {
            // Deep clone the structures and remove id fields
            const exportStructures = JSON.parse(JSON.stringify(Structures));
            Object.values(exportStructures).forEach(structArray => {
                structArray.forEach(struct => {
                    delete struct.id;
                    delete struct.created;
                    delete struct.modified;
                    delete struct.delete;
                });
            });

            const dataStr = JSON.stringify(exportStructures, null, 2);
            const dataBlob = new Blob([dataStr], {type: 'application/json'});
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.download = `rules-export-${caseID}.json`;
            link.href = url;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            alert('Failed to export structures: ' + error.message);
        }
        setExportBtnState(false);
    };

    const countExistingRules = () => {
        let count = 0;
        Object.values(Structures).forEach(structArray => {
            count += structArray.filter(struct => struct.id).length;
        });
        return count;
    };

    const handleFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            const existingCount = countExistingRules();
            if (existingCount > 0) {
                setExistingRulesCount(existingCount);
                setPendingImportFile(file);
                setShowConfirmDialog(true);
            } else {
                processImport(file);
            }
        }
        event.target.value = null; // Reset file input
    };

    const handleConfirmImport = () => {
        setShowConfirmDialog(false);
        if (pendingImportFile) {
            processImport(pendingImportFile);
            setPendingImportFile(null);
        }
    };

    const handleCancelImport = () => {
        setShowConfirmDialog(false);
        setPendingImportFile(null);
    };

    const processImport = async (file) => {
        setImportBtnState(true);
        try {
            // First, mark all existing structures for deletion
            const currentStructures = JSON.parse(JSON.stringify(Structures));
            Object.values(currentStructures).forEach(structArray => {
                structArray.forEach(struct => {
                    if (struct.id) {
                        struct.delete = true;
                    }
                });
            });
            setStructures(currentStructures);

            // Save deletions
            await saveBtn();

            // Read and process the new file
            const text = await file.text();
            const importedStructures = JSON.parse(text);

            // Mark all imported structures as new
            Object.values(importedStructures).forEach(structArray => {
                structArray.forEach(struct => {
                    struct.created = true;
                });
            });

            setStructures(importedStructures);
            fUpdate(!fU);

            // Save the new structures
            await saveBtn();
        } catch (error) {
            alert('Failed to import structures: ' + error.message);
            // Reload original data in case of error
            await loadInitialData();
        }
        setImportBtnState(false);
    };

    const updateProportions = (prop) => {
        Object.entries(prop).forEach(([idx,obj]) => {
            if (organWeights[obj.name] !== undefined) {
                organWeights[obj.name] = obj.percentage/100.0;
            }
        });
        setOrganweights(organWeights);

    };
    const calculateProportions = ()=>{
        const gradingSums = [];
        Object.entries(organWeights).forEach(([organ, rules],idx) => {
                gradingSums.push({
                    name: organ,
                    percentage: rules*100,
                    color: colorOptions[idx % colorOptions.length]
                });
        });
        return gradingSums;
    }
    return(
        <>
            <Col xs={{span: 10}}>
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <h4>Rules Overview</h4>
                            <div className="d-flex gap-2">
                                <input
                                    type="file"
                                    id="import-button"
                                    accept=".json"
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <Button
                                    onClick={() => document.getElementById('import-button').click()}
                                    disabled={importBtnState || saveBtnState}
                                >
                                    {importBtnState ? <Spinner animation="border" size="sm" /> : 'Import'}
                                </Button>
                                <Button
                                    onClick={handleExport}
                                    disabled={exportBtnState || saveBtnState}
                                >
                                    {exportBtnState ? <Spinner animation="border" size="sm" /> : 'Export'}
                                </Button>
                                <Button
                                    onClick={saveBtn}
                                    disabled={saveBtnState}
                                >
                                    {saveBtnState ? <Spinner animation="border" size="sm" /> : 'Save'}
                                </Button>
                            </div>
                        </div>
                        {/*<OrganProgressBars structures={Structures}/>*/}
                        <ProportionalSlider segments={calculateProportions()} onSlideEnd={(prop)=>updateProportions(prop)}/>
                        <RulesProvider caseID={caseid}>
                            <Stack gap={3}>
                                {Object.keys(Structures).map((Struct,index) =>
                                    <RulesListElement
                                        otherStructs={Object.keys(Structures)}
                                        key={index}
                                        structname={Struct}
                                        data={Structures[Struct]}
                                        callback={callbackRLE}
                                    />
                                )}
                            </Stack>
                        </RulesProvider>
                    </Card.Body>
                </Card>
            </Col>

            {/* Confirmation Dialog */}
            <Modal show={showConfirmDialog} onHide={handleCancelImport}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Import</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {`There are currently ${existingRulesCount} existing rule${existingRulesCount !== 1 ? 's' : ''} that will be overwritten. Do you want to proceed with the import?`}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancelImport}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleConfirmImport}>
                        Proceed
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default Rules;